import Phaser from 'phaser';
import { towerEffectIcons, type EffectRecord } from '../../config/effects';
import { ScrollList } from '../../ui/scrollList';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../../config/levels';
import { availableTowers, menuTowerTexture } from '../../config/unlocks';
import { TOWER_NAMES } from '../../config/displayNames';
import { ProgressService } from '../../services/progressService';
import { saveStorage } from '../saveStorage';
import type { TowerType } from '../../core/types';
import { getAudioService } from '../audio/AudioService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';

const GOLD = 0xf5c518;
const GOLD_HEX = '#f5c518';

/** 10 equip slots laid out as 2 rows × 5. */
const SLOTS = ProgressService.MAX_EQUIPPED; // 10
const SLOT_COLS = 5;
const SLOT_BOX = 100;
const SLOT_GAP_X = 28;
const SLOT_ROW_YS = [188, 320];
/** Slot column centres, centred on x=640. */
const SLOT_XS = ((): number[] => {
  const span = SLOT_COLS * SLOT_BOX + (SLOT_COLS - 1) * SLOT_GAP_X;
  const first = 640 - span / 2 + SLOT_BOX / 2;
  return Array.from({ length: SLOT_COLS }, (_, c) => first + c * (SLOT_BOX + SLOT_GAP_X));
})();

/**
 * Tray geometry (every tower the player owns or has earned).
 *
 * The tray used to ADAPT to the roster by shrinking its cells, and past forty
 * towers they were too small to read or grab. Since spec 2026-10-24 every cell
 * keeps the full 84px box and its name, as many columns as fit the band, and
 * the rows SCROLL inside the band between the "YOUR TOWERS" heading and the
 * footer hint — the mouse wheel or touchpad over it, or the bar on its right.
 * Dragging the tray itself does not scroll: a drag is how a tower is equipped.
 */
const TRAY_BAND_TOP = 458;
const TRAY_BAND_BOTTOM = 674;
const TRAY_BAND_W = 1140;
const TRAY_BOX = 84;
const TRAY_GAP_X = 18;
const TRAY_GAP_Y = 16;
export const TRAY_COLS = Math.floor((TRAY_BAND_W + TRAY_GAP_X) / (TRAY_BOX + TRAY_GAP_X));
const TRAY_SPAN = TRAY_COLS * TRAY_BOX + (TRAY_COLS - 1) * TRAY_GAP_X;
const TRAY_X = 640 - TRAY_SPAN / 2;
const TRAY_PITCH = TRAY_BOX + TRAY_GAP_Y;

/** Centre of tray cell `i`, in the scrolling content's own coordinates. */
export function trayCellCenter(i: number): { x: number; y: number } {
  return {
    x: (i % TRAY_COLS) * (TRAY_BOX + TRAY_GAP_X) + TRAY_BOX / 2,
    y: Math.floor(i / TRAY_COLS) * TRAY_PITCH + TRAY_BOX / 2 + 2,
  };
}

/** Height of a tray holding `count` towers, for the scroll range. */
export function trayContentHeight(count: number): number {
  return Math.ceil(Math.max(0, count) / TRAY_COLS) * TRAY_PITCH;
}

/** A rectangle used for drop hit-testing. */
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * EquipScene — curate the episode loadout.
 *
 * Ten slots (the per-episode cap) sit above a tray of every tower the player
 * owns or has earned, each shown with its tier-1 art. Drag a tray tower onto a
 * slot to equip it; onto an occupied slot to take its place (the displaced tower
 * returns to the tray); slot→slot to swap; and a slot tower out onto the tray to
 * remove it. Every change is persisted immediately, so BACK simply returns to
 * the Shop. Reached from the Shop's EQUIP button.
 */
export class EquipScene extends Phaser.Scene {
  private progress!: ProgressService;
  /** Everything the player can field, in canonical order (the tray contents). */
  private available: TowerType[] = [];
  /** Slot contents; `null` = empty. Length === SLOTS. */
  private slots: (TowerType | null)[] = [];

  /** Layers rebuilt on every change (icons); the boxes/labels are static. */
  private slotIconLayer?: Phaser.GameObjects.Container;
  /** The Hub's running effects, read once per render for the potion icons. */
  private effects: EffectRecord[] = [];
  private effectsNow = 0;
  private tray?: ScrollList;
  private counter!: Phaser.GameObjects.Text;
  /** Slot drop rectangles, indexed by slot number. */
  private slotRects: Rect[] = [];

  constructor() {
    super({ key: 'EquipScene' });
  }

  create(): void {
    this.progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
    this.available = availableTowers(this.progress.enteredLevels(), this.progress.unlockedExtras());
    // Seed the slot array from the saved (or default) loadout, left-to-right.
    const equipped = this.progress.equippedTowers(this.available);
    this.slots = Array.from({ length: SLOTS }, (_, i) => equipped[i] ?? null);
    this.slotIconLayer = undefined;
    this.slotRects = [];

    // Backdrop — reuse the Shop kitchen, dimmed for contrast.
    this.add.image(640, 360, 'bg-shop').setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.55);

    // ── Header ────────────────────────────────────────────────────────────────
    this.add
      .text(640, 44, 'EQUIP', { fontSize: '38px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(0.5);
    this.add
      .text(640, 80, 'CHOOSE UP TO 10 TOWERS FOR YOUR EPISODES', {
        fontSize: '15px',
        color: '#c8d2d8',
      })
      .setOrigin(0.5)
      .setShadow(0, 2, '#000000', 4);

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => this.scene.start('ShopScene'));

    this.counter = this.add
      .text(1200, 50, '', { fontSize: '18px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(1, 0.5);

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    // Static frames.
    this.buildSlotBoxes();
    this.add
      .text(640, 442, 'YOUR TOWERS', { fontSize: '17px', color: '#cfe0bd', fontStyle: 'bold' })
      .setOrigin(0.5);
    this.add
      .text(640, 690, 'Drag towers into the 10 slots · drag a slot out to remove.', {
        fontSize: '14px',
        color: '#b8c4c6',
        fontStyle: 'italic',
      })
      .setOrigin(0.5)
      .setShadow(0, 2, '#000000', 4);

    this.tray = new ScrollList(this, {
      x: TRAY_X,
      y: TRAY_BAND_TOP,
      width: TRAY_SPAN,
      height: TRAY_BAND_BOTTOM - TRAY_BAND_TOP,
      barX: TRAY_X + TRAY_SPAN + 20,
      barWidth: 12,
      depth: 9,
      contentDrag: false,
      cullText: true,
    });

    this.installDragHandlers();
    this.render();

    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /** Draw the 10 slot boxes once and record their drop rectangles. */
  private buildSlotBoxes(): void {
    for (let i = 0; i < SLOTS; i++) {
      const { x, y } = this.slotCenter(i);
      const g = this.add.graphics();
      g.fillStyle(0x15131f, 0.85);
      g.fillRoundedRect(x - SLOT_BOX / 2, y - SLOT_BOX / 2, SLOT_BOX, SLOT_BOX, 12);
      g.lineStyle(2, GOLD, 0.6);
      g.strokeRoundedRect(x - SLOT_BOX / 2, y - SLOT_BOX / 2, SLOT_BOX, SLOT_BOX, 12);
      this.add
        .text(x - SLOT_BOX / 2 + 8, y - SLOT_BOX / 2 + 6, `${i + 1}`, {
          fontSize: '13px',
          color: '#6f7a83',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0);
      this.slotRects[i] = { x: x - SLOT_BOX / 2, y: y - SLOT_BOX / 2, w: SLOT_BOX, h: SLOT_BOX };
    }
  }

  private slotCenter(i: number): { x: number; y: number } {
    const row = Math.floor(i / SLOT_COLS);
    const col = i % SLOT_COLS;
    return { x: SLOT_XS[col], y: SLOT_ROW_YS[row] };
  }

  /** Small potion icons over a tower's art (The Hub's running effects). */
  private addFxIcons(
    layer: Phaser.GameObjects.Container,
    type: TowerType,
    x: number,
    y: number,
    size: number,
  ): void {
    const icons = towerEffectIcons(this.effects, type, this.effectsNow).filter((k) =>
      this.textures.exists(k),
    );
    if (icons.length === 0) return;
    const gap = 3;
    const span = icons.length * size + (icons.length - 1) * gap;
    icons.forEach((key, i) => {
      const ix = x - span / 2 + size / 2 + i * (size + gap);
      layer.add(this.add.image(ix, y, key).setDisplaySize(size, size).setDepth(11));
    });
  }

  private artKey(type: TowerType): string {
    return this.textures.exists(menuTowerTexture(type)) ? menuTowerTexture(type) : 'tower-patty-1';
  }

  /** Rebuild the dynamic icon layers (slots + tray) and the counter. */
  private render(): void {
    this.effectsNow = Date.now();
    try {
      this.effects = new ProgressService(saveStorage(), LAST_LEVEL_ID).effects();
    } catch {
      this.effects = [];
    }
    this.slotIconLayer?.destroy();
    const slotLayer = this.add.container(0, 0).setDepth(10);
    this.slotIconLayer = slotLayer;
    // The tray's content is refilled in place, so its scroll position holds.
    const tray = this.tray!;
    const trayLayer = tray.content;
    trayLayer.removeAll(true);

    // Empty-slot affordance + filled-slot icons.
    for (let i = 0; i < SLOTS; i++) {
      const { x, y } = this.slotCenter(i);
      const type = this.slots[i];
      if (!type) {
        slotLayer.add(
          this.add.text(x, y, '＋', { fontSize: '30px', color: '#3f4650' }).setOrigin(0.5),
        );
        continue;
      }
      const icon = this.add.image(x, y, this.artKey(type)).setDisplaySize(76, 76);
      this.addFxIcons(slotLayer, type, x, y - 50, 30);
      icon.setData('kind', 'slot');
      icon.setData('slot', i);
      icon.setData('home', { x, y });
      icon.setInteractive({ useHandCursor: true, draggable: true });
      this.input.setDraggable(icon);
      slotLayer.add(icon);
    }

    // Tray — every available tower at a fixed grid position. Equipped towers are
    // dimmed and not draggable (their live copy lives in a slot).
    const equippedSet = new Set(this.slots.filter((t): t is TowerType => t != null));
    // Art fills the cell, leaving room for the name strip.
    const iconSize = Math.round(TRAY_BOX * 0.72);
    this.available.forEach((type, i) => {
      const { x, y } = trayCellCenter(i);
      const box = this.add
        .rectangle(x, y, TRAY_BOX, TRAY_BOX, 0x15131f, 0.7)
        .setStrokeStyle(1, GOLD, 0.35);
      trayLayer.add(box);
      const isEquipped = equippedSet.has(type);
      const icon = this.add.image(x, y - 6, this.artKey(type)).setDisplaySize(iconSize, iconSize);
      trayLayer.add(icon);
      this.addFxIcons(trayLayer, type, x, y - TRAY_BOX / 2 + 12, 22);
      trayLayer.add(
        this.add
          .text(x, y + TRAY_BOX / 2 - 12, TOWER_NAMES[type], {
            fontSize: '10px',
            color: '#c8d2d8',
            align: 'center',
            wordWrap: { width: TRAY_BOX - 6 },
          })
          .setOrigin(0.5),
      );
      if (isEquipped) {
        box.setAlpha(0.4);
        icon.setAlpha(0.35);
        trayLayer.add(
          this.add
            .text(x + TRAY_BOX / 2 - 6, y - TRAY_BOX / 2 + 4, '✓', {
              fontSize: '16px',
              color: '#4caf50',
              fontStyle: 'bold',
            })
            .setOrigin(1, 0),
        );
      } else {
        icon.setData('kind', 'tray');
        icon.setData('type', type);
        icon.setData('home', { x: x, y: y - 6 });
        icon.setInteractive({ useHandCursor: true, draggable: true });
        this.input.setDraggable(icon);
      }
    });
    tray.setContentHeight(trayContentHeight(this.available.length));

    const count = equippedSet.size;
    this.counter.setText(`${count} / ${SLOTS}`);
  }

  private installDragHandlers(): void {
    this.input.on('dragstart', (p: Phaser.Input.Pointer, obj: Phaser.GameObjects.Image) => {
      // A tray icon lives in the scrolling, MASKED tray: lift it out onto the
      // scene at the same screen spot, or it would vanish the moment it left
      // the tray for the slots above. A press on an icon scrolled out of view
      // (the mask hides it, but it still takes input) is ignored.
      const parent = obj.parentContainer;
      if (parent && parent === this.tray?.content) {
        if (!this.tray.inView(p)) obj.setData('void', true);
        const wx = obj.x + parent.x;
        const wy = obj.y + parent.y;
        parent.remove(obj);
        this.add.existing(obj);
        obj.setPosition(wx, wy);
      }
      // Follow the pointer by the grab offset, in screen coordinates.
      obj.setData('grab', { dx: p.x - obj.x, dy: p.y - obj.y });
      obj.setDepth(50).setScale(obj.scaleX * 1.08);
      if (obj.getData('void')) obj.setVisible(false);
    });
    this.input.on('drag', (p: Phaser.Input.Pointer, obj: Phaser.GameObjects.Image) => {
      const grab = obj.getData('grab') as { dx: number; dy: number } | undefined;
      obj.x = p.x - (grab?.dx ?? 0);
      obj.y = p.y - (grab?.dy ?? 0);
    });
    this.input.on('dragend', (p: Phaser.Input.Pointer, obj: Phaser.GameObjects.Image) => {
      const targetSlot = obj.getData('void') ? -1 : this.slotAt(p.x, p.y);
      if (!obj.getData('void')) this.applyDrop(obj, targetSlot);
      // Hide the dragged object immediately, then rebuild next tick — destroying
      // the in-flight drag target synchronously inside dragend upsets Phaser input.
      obj.setVisible(false);
      this.time.delayedCall(0, () => {
        // A tray icon was lifted out of the tray (dragstart), so no rebuild
        // will destroy it; a slot icon goes with its layer.
        if (!obj.parentContainer) obj.destroy();
        this.render();
      });
    });
  }

  /** Slot index whose box contains (x,y), or -1. */
  private slotAt(x: number, y: number): number {
    return this.slotRects.findIndex(
      (r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h,
    );
  }

  /** Mutate `this.slots` from a completed drag, then persist. */
  private applyDrop(obj: Phaser.GameObjects.Image, targetSlot: number): void {
    const kind = obj.getData('kind') as 'tray' | 'slot';

    if (kind === 'tray') {
      const type = obj.getData('type') as TowerType;
      if (targetSlot >= 0) {
        // Equip into the target (any tower already there is displaced to the tray).
        this.slots[targetSlot] = type;
      }
      // Dropped anywhere else: no-op (tower stays unequipped).
    } else {
      const from = obj.getData('slot') as number;
      if (targetSlot < 0) {
        // Dragged out of the slots → unequip.
        this.slots[from] = null;
      } else if (targetSlot !== from) {
        // Swap the two slots (target may be empty, which is a plain move).
        const tmp = this.slots[targetSlot];
        this.slots[targetSlot] = this.slots[from];
        this.slots[from] = tmp;
      }
    }

    this.persist();
  }

  /** Save the compacted loadout (slot order, empties removed). */
  private persist(): void {
    const list = this.slots.filter((t): t is TowerType => t != null);
    this.progress.setEquipped(list, this.available);
  }
}
